import React, { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Phone, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  isFirebaseConfigured,
  isFirebaseTestMode,
  sendPhoneOtp,
  resetRecaptchaVerifier,
} from "@/lib/firebase";
import api from "@/lib/api";

/**
 * <PhoneVerifyInline /> — Compact phone-OTP widget for embedding in forms.
 *
 * Unlike <PhoneOtpLogin /> (which handles auth + navigation), this component
 * ONLY proves phone ownership. On success it hands the parent a JWT
 * (`phone_verification_token`) that can be attached to any downstream POST.
 *
 * Three visual states:
 *   1. "enter"    → 10-digit phone Input + Send-OTP Button
 *   2. "otp"      → OTP Input + Verify Button + Resend link
 *   3. "verified" → phone is locked; a green checkmark is shown; parent can
 *                   read `verifiedPhone` + `verificationToken` from props
 *
 * Props:
 *   phone                 (controlled) 10-digit local phone value
 *   onPhoneChange         setter used only in "enter" state
 *   onVerified            ({phone, verification_token}) => void
 *   onReset               ()   => void — called when user clicks "Change number"
 *   verificationToken     truthy → widget is in "verified" state (parent may
 *                                  persist the token across un/remounts)
 *   idSuffix              string — used to build a unique reCAPTCHA container
 *                                  id when this widget is used more than once
 *                                  on a page (e.g. in multiple dialogs)
 */
export default function PhoneVerifyInline({
  phone,
  onPhoneChange,
  onVerified,
  onReset,
  verificationToken,
  idSuffix = "default",
}) {
  const recaptchaContainerId = `psg-booking-recaptcha-${idSuffix}`;

  const [step, setStep] = useState(verificationToken ? "verified" : "enter");
  const [otp, setOtp] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const configured = isFirebaseConfigured();

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; resetRecaptchaVerifier(); }, []);

  // 30-second resend countdown
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setInterval(() => setResendIn((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const digits = (phone || "").replace(/\D/g, "").slice(0, 10);
  const canSend = configured && digits.length === 10 && !sending;

  const sendOtp = useCallback(async () => {
    if (!canSend) {
      if (!configured) toast.error("Phone verification isn't configured on this site. Please contact support.");
      else toast.error("Enter a valid 10-digit mobile number first.");
      return;
    }
    setSending(true);
    try {
      const e164 = `+91${digits}`;
      const result = await sendPhoneOtp(e164, recaptchaContainerId);
      setConfirmation(result);
      setStep("otp");
      setResendIn(30);
      toast.success("OTP sent to your phone.");
    } catch (err) {
      const code = err?.code || "";
      let msg = "Could not send OTP. Please try again.";
      if (code === "auth/invalid-phone-number") msg = "Phone number is invalid.";
      else if (code === "auth/too-many-requests") msg = "Too many attempts. Please wait a few minutes.";
      else if (code === "auth/captcha-check-failed") msg = "reCAPTCHA check failed. Please retry.";
      else if (code === "auth/quota-exceeded") msg = "Daily OTP quota exceeded. Try again tomorrow.";
      else if (err?.message) msg = err.message;
      console.error("[BookingPhoneVerify] sendOtp failed", { code, err });
      toast.error(msg);
      resetRecaptchaVerifier();
    } finally {
      setSending(false);
    }
  }, [canSend, configured, digits, recaptchaContainerId]);

  const verifyOtp = useCallback(async () => {
    if (!confirmation) return toast.error("Please request an OTP first.");
    if (otp.length < 6) return toast.error("Enter the 6-digit OTP.");
    setVerifying(true);
    try {
      const credential = await confirmation.confirm(otp);
      const idToken = await credential.user.getIdToken();
      const res = await api.post("/auth/phone/verify", {
        firebase_id_token: idToken,
        mode: "booking",
      });
      toast.success("Phone verified!");
      setStep("verified");
      onVerified?.({
        phone: res.data.phone,
        verification_token: res.data.verification_token,
      });
    } catch (err) {
      const code = err?.code || "";
      let msg = err?.response?.data?.detail || "Invalid OTP. Please try again.";
      if (code === "auth/invalid-verification-code") msg = "That OTP is incorrect.";
      else if (code === "auth/code-expired") msg = "That OTP has expired. Please request a new one.";
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  }, [confirmation, otp, onVerified]);

  const changeNumber = useCallback(() => {
    setStep("enter");
    setOtp("");
    setConfirmation(null);
    resetRecaptchaVerifier();
    onReset?.();
  }, [onReset]);

  if (!configured) {
    return (
      <div
        className="rounded-md border border-dashed border-[#E7DDCF] bg-[#FBF7F1] p-3 text-xs text-[#7A5A22]"
        data-testid="booking-phone-misconfigured"
      >
        Phone verification is not configured on this site — booking is temporarily unavailable.
      </div>
    );
  }

  // ── State: verified ────────────────────────────────────────────────────
  if (step === "verified") {
    return (
      <div className="space-y-1.5">
        <Label className="text-xs">Phone *</Label>
        <div
          className="flex items-center justify-between gap-2 h-10 px-3 rounded-md border border-[rgba(31,78,61,0.35)] bg-[rgba(31,78,61,0.06)]"
          data-testid="booking-phone-verified"
        >
          <div className="flex items-center gap-2 text-sm text-[#1F4E3D]">
            <CheckCircle2 size={16} className="fill-[#1F4E3D] text-white" />
            <span>+91 {digits}</span>
            <span className="text-[10px] uppercase tracking-widest text-[#1F4E3D]/70">Verified</span>
          </div>
          <button
            type="button"
            onClick={changeNumber}
            className="text-[11px] text-[#7A5A22] hover:text-[#1F4E3D] underline"
            data-testid="booking-phone-change-button"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  // ── State: otp entry ───────────────────────────────────────────────────
  if (step === "otp") {
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={changeNumber}
          className="inline-flex items-center gap-1 text-[11px] text-[#7A5A22] hover:text-[#1F4E3D]"
          data-testid="booking-phone-back-button"
        >
          <ArrowLeft size={12} /> Change number
        </button>
        <Label className="text-xs">Enter the 6-digit OTP sent to +91 {digits}</Label>
        <Input
          inputMode="numeric"
          placeholder="123456"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          maxLength={6}
          className="text-center tracking-[0.4em] text-lg font-medium"
          data-testid="booking-otp-input"
          autoComplete="one-time-code"
          autoFocus
        />
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            onClick={verifyOtp}
            disabled={verifying || otp.length < 6}
            className="flex-1 h-10 bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827] gap-2"
            data-testid="booking-verify-otp-button"
          >
            {verifying ? <><Loader2 size={14} className="animate-spin" /> Verifying…</> : "Verify"}
          </Button>
          {resendIn > 0 ? (
            <span className="text-[11px] text-[#4B5563]" data-testid="booking-resend-countdown">
              Resend in {resendIn}s
            </span>
          ) : (
            <button
              type="button"
              onClick={sendOtp}
              className="text-[11px] text-[#1F4E3D] hover:underline"
              data-testid="booking-resend-otp-button"
            >
              Resend OTP
            </button>
          )}
        </div>
        <div id={recaptchaContainerId} data-testid={`recaptcha-container-${idSuffix}`} />
      </div>
    );
  }

  // ── State: enter phone ─────────────────────────────────────────────────
  return (
    <div className="space-y-1.5">
      {isFirebaseTestMode() && (
        <div className="rounded-md border border-dashed border-[#E7DDCF] bg-[#FBF7F1] px-2.5 py-1.5 text-[10px] text-[#7A5A22]" data-testid="booking-firebase-test-mode-banner">
          Test mode — use <code className="font-mono">+91 9930834774</code> / OTP <code className="font-mono">123456</code>.
        </div>
      )}
      <Label className="text-xs">Phone * (needs OTP verification)</Label>
      <div className="flex gap-2">
        <div className="flex items-center px-3 rounded-md border border-[#E7DDCF] bg-[#F5EFE6] text-sm text-[#4B5563]">
          +91
        </div>
        <Input
          inputMode="numeric"
          placeholder="10-digit mobile"
          value={digits}
          onChange={(e) => onPhoneChange?.(e.target.value.replace(/\D/g, "").slice(0, 10))}
          className="flex-1"
          data-testid="booking-phone-input"
          autoComplete="tel-national"
        />
      </div>
      <Button
        type="button"
        onClick={sendOtp}
        disabled={!canSend}
        className="w-full h-9 bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2 text-xs"
        data-testid="booking-send-otp-button"
      >
        {sending ? <><Loader2 size={14} className="animate-spin" /> Sending OTP…</> : <><Phone size={14} /> Send OTP to Verify</>}
      </Button>
      <div id={recaptchaContainerId} data-testid={`recaptcha-container-${idSuffix}`} />
    </div>
  );
}
